const median = (values = []) => {
  const numeric = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!numeric.length) return null;
  const middle = Math.floor(numeric.length / 2);
  return numeric.length % 2 === 1 ? numeric[middle] : (numeric[middle - 1] + numeric[middle]) / 2;
};

const mostFrequent = (values = []) => {
  const counts = new Map();
  values.filter(Boolean).forEach((value) => {
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  let winner = '';
  let winnerCount = 0;
  counts.forEach((count, value) => {
    if (count > winnerCount || (count === winnerCount && value.localeCompare(winner) < 0)) {
      winner = value;
      winnerCount = count;
    }
  });

  return winner;
};

const bucketSeverity = (value) => {
  if (!Number.isFinite(value)) return '';
  if (value <= 2) return 'low';
  if (value <= 5) return 'medium';
  return 'high';
};

export const getWindowDaysForCategory = (category) => (category === 'sleep' ? 7 : 14);

const buildObservationSummary = (observations = []) => ({
  count: observations.length,
  entryIds: observations.map((observation) => observation.id),
});

export const buildBehaviorSummary = (observations = []) => {
  const severityValues = observations.map((observation) => observation.severity).filter((value) => Number.isFinite(value));
  return {
    ...buildObservationSummary(observations),
    metrics: {
      medianSeverity: median(severityValues),
      dominantSeverityBucket: mostFrequent(severityValues.map(bucketSeverity)),
      severityValues,
    },
  };
};

export const buildSleepSummary = (observations = []) => {
  const durations = observations.map((observation) => observation.durationMinutes).filter((value) => Number.isFinite(value));
  const qualities = observations.map((observation) => observation.quality).filter(Boolean);
  return {
    ...buildObservationSummary(observations),
    metrics: {
      medianDurationMinutes: median(durations),
      dominantQuality: mostFrequent(qualities),
      durationValues: durations,
      qualityValues: qualities,
    },
  };
};

export const buildMoodSummary = (observations = []) => {
  const values = observations.map((observation) => observation.moodValue).filter(Boolean);
  return {
    ...buildObservationSummary(observations),
    metrics: {
      dominantMood: mostFrequent(values),
      moodValues: values,
    },
  };
};

export const buildFoodSummary = (observations = []) => {
  const values = observations.map((observation) => observation.appetite).filter(Boolean);
  const portionValues = observations.map((observation) => observation.portion).filter(Boolean);
  const reactionValues = observations.map((observation) => observation.reaction).filter(Boolean);
  return {
    ...buildObservationSummary(observations),
    metrics: {
      dominantAppetite: mostFrequent(values),
      appetiteValues: values,
      portionValues,
      reactionValues,
    },
  };
};

export const getSummaryForCategory = (category, observations = []) => {
  if (category === 'behavior') return buildBehaviorSummary(observations);
  if (category === 'sleep') return buildSleepSummary(observations);
  if (category === 'mood') return buildMoodSummary(observations);
  return buildFoodSummary(observations);
};

export const hasMeaningfulChange = (category, before, after) => {
  if (category === 'behavior') {
    return before.metrics.dominantSeverityBucket !== after.metrics.dominantSeverityBucket;
  }

  if (category === 'sleep') {
    const durationShift = Number.isFinite(before.metrics.medianDurationMinutes)
      && Number.isFinite(after.metrics.medianDurationMinutes)
      && Math.abs(after.metrics.medianDurationMinutes - before.metrics.medianDurationMinutes) >= 60;
    const qualityShift = before.metrics.dominantQuality && after.metrics.dominantQuality
      && before.metrics.dominantQuality !== after.metrics.dominantQuality;
    return durationShift || qualityShift;
  }

  if (category === 'mood') {
    return Boolean(before.metrics.dominantMood && after.metrics.dominantMood && before.metrics.dominantMood !== after.metrics.dominantMood);
  }

  return Boolean(before.metrics.dominantAppetite && after.metrics.dominantAppetite && before.metrics.dominantAppetite !== after.metrics.dominantAppetite);
};

export const buildPatternSummary = (category) => {
  if (category === 'behavior') return 'Behavior severity shifted across the medication change window.';
  if (category === 'sleep') return 'Sleep duration or quality shifted across the medication change window.';
  if (category === 'mood') return 'Mood shifted across the medication change window.';
  return 'Appetite pattern shifted across the medication change window.';
};

export const buildRuleMatched = (category) => {
  if (category === 'behavior') return 'behavior_severity_bucket_change';
  if (category === 'sleep') return 'sleep_duration_or_quality_change';
  if (category === 'mood') return 'mood_dominant_label_change';
  return 'appetite_dominant_bucket_change';
};

export const buildEvidenceEntries = (observations = [], category) => observations.map((observation) => {
  const fields = category === 'behavior'
    ? {
        severity: observation.severity,
        severityLabel: observation.severityLabel || null,
        triggerSummary: observation.triggerSummary || null,
        triggerNotes: observation.triggerNotes || null,
        remedy: observation.remedy || null,
      }
    : category === 'sleep'
      ? {
          sleepDetails: {
            duration: observation.durationMinutes,
            quality: observation.quality || null,
            type: observation.type || null,
            bedtime: observation.bedtime || null,
            wakeTime: observation.wakeTime || null,
            disturbances: observation.disturbances || null,
          },
        }
      : category === 'mood'
        ? {
            moodValue: observation.moodValue,
          }
        : {
            foodDetails: {
              mealType: observation.mealType,
              portion: observation.portion || null,
              reaction: observation.reaction || null,
            },
            appetite: observation.appetite,
          };

  return {
    entryId: observation.id,
    occurredAt: observation.occurredAt.toISOString(),
    fields,
  };
});
